// React
import { useGeolocated } from "react-geolocated";
// React Leaflet
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
// Components
import UserMarker from "../components/map/UserMarker";
import ClickMarker from "../components/map/ClickMarker";
import SearchPanel from "../components/map/SearchPanel";
import HelpModal from "../components/map/HelpModal";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  LocationLoading,
  LocationNotSupported,
  LocationDenied,
  LocationError,
} from "../components/map/GeolocationStatus";
// Assets
import logo from "../assets/images/NAERLS-Logo.png";
// Constants
import studyAreaBoundaries from "../constants/studyAreaBoundaries.json";
import isWithinStudyArea from "../constants/studyArea";
// Styles
import "../styles/mapStyles/Map.css";
import "../styles/mapStyles/SearchModeToggle.css";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function MapPage() {
  // Chrome on desktop and mobile both include "Chrome" in the user agent,
  // but we exclude Edge and Opera which also contain "Chrome" in their agent.
  const isChrome =
    /Chrome/.test(navigator.userAgent) &&
    !/Edg/.test(navigator.userAgent) &&
    !/OPR/.test(navigator.userAgent);

  // Chrome takes significantly longer to resolve location permission, so we give it 30 seconds. Other browsers resolve much faster at 10 seconds.
  const { coords, isGeolocationAvailable, isGeolocationEnabled, positionError } = useGeolocated({
    positionOptions: { enableHighAccuracy: true, maximumAge: 0, timeout: isChrome ? 30000 : 10000 },
    watchPosition: true,
    userDecisionTimeout: isChrome ? 30000 : 10000,
  });

  // Geolocation status handling with dedicated UI components for each state.

  // Browser doesn't support geolocation.
  if (!isGeolocationAvailable) return <LocationNotSupported />; 
  // Waiting for user to allow geolocation and for coordinates to be obtained.
  if (!coords && !positionError && isGeolocationEnabled !== false) return <LocationLoading />; 
  // User denied geolocation permission.
  if (isGeolocationEnabled === false) return <LocationDenied />; 
   // An error occurred while trying to obtain geolocation (could be due to timeout or other issues).
  if (positionError) return <LocationError message={positionError?.message} />;
  // Fallback loading state while waiting for coordinates, just in case.
  if (!coords) return <LocationLoading />; 

  const userLocation = [coords.latitude, coords.longitude];
  const isInStudyArea = isWithinStudyArea(userLocation[0], userLocation[1]);
  const defaultCenter = isInStudyArea ? userLocation : [11.0, 7.75];

  return (
    <div className="map-wrapper">
      {/* Language switcher (navbar is hidden on /map) */}
      <LanguageSwitcher variant="floating" />

      {/* NAERLS logo */}
      <img src={logo} alt="NAERLS Logo" className="map-logo" />

      {/* Search panel */}
      <SearchPanel/>

      {/* User Guide */}
      <HelpModal />

      {/* Map */}
      <MapContainer center={defaultCenter} zoom={7} className="map-container">

        {/* Google Maps tile layer with proper attribution */}
        <TileLayer
          url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
          attribution='<img src="https://www.gstatic.com/images/branding/product/1x/googleg_16dp.png" alt="Google" style="height: 16px; vertical-align: middle; margin-right: 4px;" /> Google'
        />

        {/* Study area boundaries */}
        <GeoJSON
          data={studyAreaBoundaries}
          style={(feature) => ({
            color: feature?.properties?.shapeColor || "#f4a261",
            weight: 3.5,
            opacity: 0.9,
            fillColor: feature?.properties?.shapeColor || "#f4a261",
            fillOpacity: 0.5,
            dashArray: "5, 3",
          })}
        />

        {/* User marker */}
        {isInStudyArea && <UserMarker userLocation={userLocation} />}

        {/* Click marker */}
        <ClickMarker />
      </MapContainer>
    </div>
  );
}

export default MapPage;
